import { useRouteContext } from '@/app/layouts/TenantLayout'
import { isOrganizationLevelRoute, toOrgSegment, toTenantSegment } from '@/lib/routeScope'
import { getAccessibleTenants, isPlatformUser, resolveDefaultTenantForOrg } from '@/stores/auth'
import { useAuthStore, useSwitchTenant } from '@/stores/authStore'
import { isConsoleDeployment } from '@/utils/deployment'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { OrgSwitcherDropdown } from './OrgSwitcherDropdown'
import { ProjectSwitcherDropdown } from './ProjectSwitcherDropdown'
import { useCurrentOrgInfo, useOrganizations } from './useSwitcherData'

interface OrgProjectSwitcherProps {
	currentOrgId?: string | null
	onOrganizationSelect?: (orgId: string) => void
	onProjectSelect?: (tenantId: string) => void
	showProjectSwitcher?: boolean
}

export function OrgProjectSwitcher({
	currentOrgId: propOrgId,
	onOrganizationSelect,
	onProjectSelect,
	showProjectSwitcher = true,
}: OrgProjectSwitcherProps) {
	const navigate = useNavigate()
	const location = useLocation()
	const switchTenant = useSwitchTenant()

	const routeContext = useRouteContext()
	const orgId = routeContext?.orgId || null
	const tenantId = routeContext?.tenantId || null
	const currentOrgId = propOrgId !== undefined ? propOrgId : orgId

	const user = useAuthStore(s => s.user)
	const platformUser = user && isPlatformUser(user) ? user : null
	const hasProjects = platformUser
		? getAccessibleTenants(platformUser).filter(t => t.org_id === currentOrgId).length > 0
		: false

	const isOrgLevel = isOrganizationLevelRoute(location.pathname)
	const isProjectLevel = !!tenantId && !isOrgLevel

	const isOrgActive = isOrgLevel || (!hasProjects && !isProjectLevel)
	const isProjectActive = isProjectLevel || (hasProjects && !isOrgLevel)

	const currentOrg = useCurrentOrgInfo(currentOrgId)
	const { organizations } = useOrganizations()

	const findTargetProject = useCallback((targetOrgId: string) => {
		const state = useAuthStore.getState()
		const user = state.user
		if (!user || !isPlatformUser(user)) {
			return null
		}

		const currentTenant = state.currentTenant
		if (currentTenant) {
			const belongsToOrg = user.tenant_list?.some(t => t.id === currentTenant && t.org_id === targetOrgId)
			if (belongsToOrg) {
				return currentTenant
			}
		}

		return resolveDefaultTenantForOrg(user, targetOrgId)
	}, [])

	const handleOrganizationSelect = useCallback(
		(selectedOrgId: string) => {
			// No-op only when same org is already selected and the route has no project segment.
			if (selectedOrgId === currentOrgId && !routeContext?.tenantId) {
				return
			}

			const org = organizations.find(o => o.id === selectedOrgId)
			const orgName = org?.name || selectedOrgId
			toast.info(`Switching to organization "${orgName}"`)

			useAuthStore.getState().setCurrentOrganizationId(selectedOrgId)
			useAuthStore.getState().clearCurrentTenantUnlessBelongsToOrg(selectedOrgId)

			const targetProject = platformUser ? resolveDefaultTenantForOrg(platformUser, selectedOrgId) : null

			if (onOrganizationSelect) {
				onOrganizationSelect(selectedOrgId)
			} else if (isConsoleDeployment()) {
				navigate({
					to: '/orgs/$orgId/billing/$type',
					params: { orgId: toOrgSegment(selectedOrgId), type: 'plan-details' },
				})
			} else if (targetProject) {
				navigate({
					to: '/orgs/$orgId/projects/$tenantId',
					params: { orgId: toOrgSegment(selectedOrgId), tenantId: toTenantSegment(targetProject) },
				})
			} else {
				navigate({
					to: '/orgs/$orgId/inventory',
					params: { orgId: toOrgSegment(selectedOrgId) },
				})
			}
		},
		[currentOrgId, routeContext?.tenantId, onOrganizationSelect, navigate, organizations, platformUser],
	)

	const handleOrgNavigate = useCallback(() => {
		if (!currentOrgId) {
			return
		}

		if (isConsoleDeployment()) {
			navigate({
				to: '/orgs/$orgId/billing/$type',
				params: { orgId: toOrgSegment(currentOrgId), type: 'plan-details' },
			})
		} else {
			navigate({
				to: '/orgs/$orgId/inventory',
				params: { orgId: toOrgSegment(currentOrgId) },
			})
		}
	}, [currentOrgId, navigate])

	const handleProjectNavigate = useCallback(() => {
		if (!currentOrgId) {
			return
		}

		if (tenantId) {
			return
		}

		const targetProject = findTargetProject(currentOrgId)
		if (targetProject) {
			switchTenant(targetProject, navigate)
		}
	}, [currentOrgId, tenantId, findTargetProject, switchTenant, navigate])

	const handleProjectSelect = useCallback(
		(tenantId: string) => {
			if (onProjectSelect) {
				onProjectSelect(tenantId)
			}
		},
		[onProjectSelect],
	)

	return (
		<div className='flex flex-col gap-0.5 w-full rounded-lg bg-sidebar-accent/50 p-1'>
			<OrgSwitcherDropdown
				currentOrgId={currentOrgId}
				currentOrgName={currentOrg.name}
				onOrganizationSelect={handleOrganizationSelect}
				onNavigate={handleOrgNavigate}
				isActive={isOrgActive}
			/>

			{showProjectSwitcher && (
				<ProjectSwitcherDropdown
					onProjectSelect={onProjectSelect && handleProjectSelect}
					onNavigate={handleProjectNavigate}
					isActive={isProjectActive}
				/>
			)}
		</div>
	)
}
